import {useMemo} from "react";
import {AnankeSkeleton} from "@vesta/ananke/components/anankeSkeleton";
import {AnankeText} from "@vesta/ananke/components/anankeText";
import {TableErrorComponent} from "~/components/ottopilotDataViewer/tableErrorComponent";
import {ActivityTimelineDaySection} from "~/modules/activityTimeline/cards/ActivityLogCardShell";
import {limitTimelineDayGroups} from "~/modules/activityTimeline/timeline/timelineGenerator";
import type {TimelineDayGroup} from "~/modules/activityTimeline/types/activityTimeline.types";

function ProfileCustomerActivityTimelineSkeleton() {
    return (
        <div className="grid w-full min-w-0 gap-ds-space-150">
            <AnankeSkeleton width="28%" height={14} />
            <AnankeSkeleton width="100%" height={52} borderRadius="4px" />
            <AnankeSkeleton width="100%" height={52} borderRadius="4px" />
        </div>
    );
}

export function ProfileCustomerActivityTimeline({
    groups,
    isLoading,
    isError,
    maxItems,
    className,
}: {
    groups: Array<TimelineDayGroup>;
    isLoading: boolean;
    isError?: boolean;
    maxItems?: number;
    className?: string;
}) {
    const displayGroups = useMemo(() => {
        if (maxItems === undefined) {
            return groups;
        }
        return limitTimelineDayGroups(groups, maxItems);
    }, [groups, maxItems]);

    if (isLoading === true) {
        return <ProfileCustomerActivityTimelineSkeleton />;
    }

    if (isError === true) {
        return (
            <div className="w-full min-w-0">
                <TableErrorComponent error="Failed to load activity" />
            </div>
        );
    }

    if (displayGroups.length === 0) {
        return <AnankeText color="color.text.subtlest">No activity yet.</AnankeText>;
    }

    return (
        <div className={`grid w-full min-w-0 gap-ds-space-200 ${className ?? ""}`.trim()}>
            {displayGroups.map((group) => (
                <ActivityTimelineDaySection
                    key={group.dateKey}
                    label={group.label}
                    items={group.items}
                    defaultOpen={group.defaultOpen ?? true}
                />
            ))}
        </div>
    );
}
